import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { syncAppBadgeAll } from '@/lib/faviconBadge';
import {
  Bell, CheckCircle, XCircle, AlertTriangle, Users, IndianRupee, CalendarDays,
  TrendingUp, ClipboardCheck, CreditCard, MessageSquare, Shield, FileText,
  Trophy, Clock, UserPlus, Check, Trash2
} from 'lucide-react';

/**
 * Phase 282 — Strict PWA badge syncing.
 * Posts to the registered service worker so the OS-level app icon badge
 * always mirrors the server-side unread count. Badge ONLY clears on
 * explicit notification-center interaction (mark-read / read-all /
 * clear-all) — never on tab focus, per the executive UX call.
 */
const syncAppBadge = (count) => {
  try {
    // Phase 299.a — Vibrant red canvas-favicon + mobile-OS native badge.
    syncAppBadgeAll(count);
    // Legacy SW route — keep firing it for installed PWAs whose cached SW
    // still handles the old 'set-badge' message contract.
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
    const sw = navigator.serviceWorker.controller;
    if (!sw) return;
    const safe = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
    if (safe === 0) {
      sw.postMessage({ type: 'clear-badge' });
    } else {
      sw.postMessage({ type: 'set-badge', count: safe });
    }
  } catch (_) { /* best-effort */ }
};

const iconMap = {
  'alert-triangle': AlertTriangle,
  'clipboard-check': ClipboardCheck,
  'indian-rupee': IndianRupee,
  'check-circle': CheckCircle,
  'user-plus': UserPlus,
  'users': Users,
  'calendar-days': CalendarDays,
  'trending-up': TrendingUp,
  'credit-card': CreditCard,
  'message-square': MessageSquare,
  'x-circle': XCircle,
  'clock': Clock,
  'trophy': Trophy,
  'shield': Shield,
  'file-text': FileText,
  'bell': Bell,
};

const priorityColors = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

const priorityDot = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-blue-500',
};

export function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Phase 324c — Pop animation when the unread count increments.
  const [pop, setPop] = useState(false);
  const prevCountRef = useRef(0);
  const popTimerRef = useRef(null);

  useEffect(() => {
    // Trigger the pop only on INCREMENT (new notification arrived). Skip the
    // initial 0 → n hydration on first mount so the bell doesn't pop just
    // because there were already unread items when the page loaded.
    if (unreadCount > prevCountRef.current && prevCountRef.current > 0) {
      setPop(true);
      if (popTimerRef.current) clearTimeout(popTimerRef.current);
      popTimerRef.current = setTimeout(() => setPop(false), 600);
    }
    prevCountRef.current = unreadCount;
    return () => {
      if (popTimerRef.current) clearTimeout(popTimerRef.current);
    };
  }, [unreadCount]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications', { params: { limit: 20 } });
      setNotifications(res.data.notifications || []);
      const next = res.data.unread_count || 0;
      setUnreadCount(next);
      // Phase 282 — sync OS-level PWA badge on initial fetch + every refetch
      // so the home-screen icon counter reflects server-side reality from
      // first paint (not just from the 30s poll cycle onward).
      syncAppBadge(next);
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => {
    // Phase 264 — defer the initial fetch off the synchronous effect tree so
    // we don't trip the `react-hooks/set-state-in-effect` rule (the network
    // round-trip already runs async, but the rule still flags the synchronous
    // effect invocation pattern).
    const t = setTimeout(() => { fetchNotifications(); }, 0);
    return () => clearTimeout(t);
  }, [fetchNotifications]);

  // Poll every 30s — same defer trick keeps setState outside the effect's
  // synchronous frame.
  useEffect(() => {
    let cancelled = false;
    const interval = setInterval(() => {
      api.get('/notifications', { params: { unread_only: true, limit: 5 } })
        .then(res => {
          if (cancelled) return;
          const next = res.data.unread_count || 0;
          setTimeout(() => {
            if (!cancelled) {
              setUnreadCount(next);
              syncAppBadge(next);
            }
          }, 0);
        })
        .catch(() => {});
    }, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => {
        const next = Math.max(0, prev - 1);
        syncAppBadge(next);
        return next;
      });
    } catch (err) { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    setLoading(true);
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      syncAppBadge(0);
    } catch (err) { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleClearAll = async () => {
    // Phase 124B + Phase 264 — Permanently purge stale notification rows.
    // The backend uses the same visibility branches as GET /notifications,
    // so this only deletes records the user can see. The frontend MUST
    // await server confirmation before clearing local state to prevent
    // zombie repopulation after page refresh.
    if (!window.confirm('Clear all notifications? This permanently removes every notification visible to you.')) return;
    setLoading(true);
    try {
      const res = await api.post('/notifications/clear-all');
      setNotifications([]);
      setUnreadCount(0);
      syncAppBadge(0);
      const purged = res?.data?.cleared ?? 0;
      toast.success(purged > 0 ? `${purged} notification${purged === 1 ? '' : 's'} cleared` : 'Notifications cleared');
      // Phase 264 — re-fetch in case the server keeps system broadcasts
      // resident in the user's feed. This makes the UI reflect the true
      // server-side state, NOT a stale optimistic empty.
      await fetchNotifications();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not clear notifications');
    } finally { setLoading(false); }
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              className={`absolute -top-1 -right-1 min-w-[20px] h-5 px-1 text-white text-[11px] font-extrabold rounded-full flex items-center justify-center animate-pulse ring-2 ring-white dark:ring-slate-900 shadow-[0_0_10px_rgba(244,63,94,0.6)] ${pop ? 'badge-pop' : ''}`}
              style={{ background: 'linear-gradient(135deg, #F43F5E 0%, #F97316 100%)' }}
              data-testid="notification-bell-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end" data-testid="notification-panel">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Notifications</h3>
            {unreadCount > 0 && <Badge variant="destructive" className="text-[10px] h-5">{unreadCount}</Badge>}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleMarkAllRead} disabled={loading} data-testid="notif-mark-all-read">
                <Check className="h-3 w-3 mr-1" /> Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                onClick={handleClearAll}
                disabled={loading}
                data-testid="notif-clear-all"
                title="Permanently clear all visible notifications"
              >
                <Trash2 className="h-3 w-3 mr-1" /> Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <ScrollArea className="max-h-[400px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Bell className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map(notif => {
                const IconComp = iconMap[notif.icon] || Bell;
                const dotColor = priorityDot[notif.priority] || priorityDot.low;
                return (
                  <div
                    key={notif.id}
                    className={`flex gap-3 px-4 py-3 transition-colors duration-150 cursor-pointer hover:bg-muted/50 ${!notif.read ? 'bg-primary/5' : ''}`}
                    onClick={() => !notif.read && handleMarkRead(notif.id)}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className={`h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 ${priorityColors[notif.priority] || priorityColors.low}`}>
                      <IconComp className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-snug ${!notif.read ? 'font-medium' : 'text-muted-foreground'}`}>
                          {notif.message}
                        </p>
                        {!notif.read && <span className={`h-2 w-2 rounded-full flex-shrink-0 mt-1.5 ${dotColor}`} />}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-muted-foreground">{timeAgo(notif.created_at)}</span>
                        <span className="text-[10px] text-muted-foreground capitalize">• {notif.type.replace(/_/g, ' ')}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

export default NotificationBell;
